/* eslint-disable no-param-reassign */

import { JiraSiteAri } from '@atlassian/ari/jira/site';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fetchIssueRelayData,
	getAggDataFromInitialPageResponse,
	fetchIssueRelayDataViaDelegate,
	fetchIssueAggData,
} from '@atlassian/jira-issue-fetch-services-vendorless/src/services/issue-agg-data/index.tsx';
import {
	fetchIssueGiraData,
	getGiraDataFromInitialPageResponse,
} from '@atlassian/jira-issue-fetch-services-vendorless/src/services/issue-gira-data/index.tsx';
import { fetchIssueGiraNonCriticalData } from '@atlassian/jira-issue-fetch-services-vendorless/src/services/issue-gira-non-critical-data/index.tsx';
import getMeta from '../get-meta.tsx';

const EARLY_FETCH_GIRA_KEY = 'ISSUE_GIRA_DATA';
const EARLY_FETCH_AGG_KEY = 'ISSUE_AGG_DATA';
const EARLY_FETCH_GIRA_NON_CRITICAL = 'ISSUE_GIRA_NON_CRITICAL_DATA';

// From strip-url-to-issue-key
const ISSUE_KEY_REGEXP = /(([A-Z0-9][A-Z0-9_]+)-([0-9]+))($|[?|#].*|\/embed)/;

export const stripUrlToIssueKey = (candidateUrl: string): string | null => {
	const match = candidateUrl.match(ISSUE_KEY_REGEXP);
	return match && match[1];
};

type WindowVariables = {
	__SSR_FALLBACK__?: boolean;
};

export const fetchIssueData = (win: typeof window) => {
	if (expVal('issue_view_ssr_perf_m2', 'isEnabled', false)) {
		return;
	}

	const { fetch, location } = win;
	if (!fetch) {
		return;
	}

	const { pathname, href } = location;
	if (!/^\/browse\/(?:\w+-\d+)/.test(pathname)) {
		return;
	}

	const issueKey = stripUrlToIssueKey(href);
	if (issueKey === null || issueKey === undefined) {
		return;
	}

	const projectKey = issueKey != null ? issueKey.split('-')[0] : '';

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const globalVar = (typeof window !== 'undefined'
		? window
		: globalThis) as unknown as WindowVariables;

	// While falling back, certain document events are fired before this scripts is ran,
	// This causes certain functionality to break. We're setting a __SSR_FALLBACK__ to true when falling back.
	// This allows us to disable certain functionality that relies on document events and requires SSR to begin with.
	const isSsrFallback = globalVar?.__SSR_FALLBACK__ === true;

	performance.mark('issueViewEarlyScripts:clientRequestStart');

	performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_GIRA_KEY}:clientRequestStart`);
	if (!isSsrFallback && fg('jira-bifrost-template-early-gql-fetch-master')) {
		const issueGiraDataPromise = getGiraDataFromInitialPageResponse().catch(() => {
			performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_GIRA_KEY}:getFromInitialPageFailed`);
			return fetchIssueGiraData('', issueKey, projectKey);
		});
		issueGiraDataPromise.then(() =>
			performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_GIRA_KEY}:getFromInitialPageEnd`),
		);
		win[EARLY_FETCH_GIRA_KEY] = issueGiraDataPromise;
	} else {
		const issueGiraDataPromise = fetchIssueGiraData('', issueKey, projectKey);
		issueGiraDataPromise.then(() =>
			performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_GIRA_KEY}:getFromNetworkEnd`),
		);
		win[EARLY_FETCH_GIRA_KEY] = issueGiraDataPromise;
	}
	performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_GIRA_KEY}:clientRequestEnd`);

	performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_AGG_KEY}:clientRequestStart`);
	const cloudId = getMeta('ajs-cloud-id') || '';
	const siteAri = JiraSiteAri.create({ siteId: cloudId }).toString();
	if (!isSsrFallback && fg('jira-bifrost-template-early-gql-fetch-master')) {
		const issueAggDataPromise = getAggDataFromInitialPageResponse().catch(() => {
			performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_AGG_KEY}:getFromInitialPageFailed`);
			// can't use fetchIssueRelayData here otherwise there will be a race to set the same promise map
			return fetchIssueAggData(issueKey, cloudId, siteAri);
		});
		issueAggDataPromise.then(() => {
			performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_AGG_KEY}:getFromInitialPageEnd`);
		});
		fetchIssueRelayDataViaDelegate(issueKey, cloudId, siteAri, issueAggDataPromise);
	} else {
		fetchIssueRelayData(issueKey, cloudId, siteAri);
	}
	performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_AGG_KEY}:clientRequestEnd`);
	if (!fg('move_iv_noncriticalgiraquery_out_of_critical_path')) {
		performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_GIRA_NON_CRITICAL}:clientRequestStart`);

		const issueGiraNonCriticalDataPromise = fetchIssueGiraNonCriticalData(issueKey);
		issueGiraNonCriticalDataPromise.then(() =>
			performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_GIRA_NON_CRITICAL}:getFromNetworkEnd`),
		);
		win[EARLY_FETCH_GIRA_NON_CRITICAL] = issueGiraNonCriticalDataPromise;
		performance.mark(`issueViewEarlyScripts:${EARLY_FETCH_GIRA_NON_CRITICAL}:clientRequestEnd`);
	}

	performance.mark('issueViewEarlyScripts:clientRequestEnd');
};
